import { ChangeDetectionStrategy, Component, computed, ElementRef, ErrorHandler, Inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { form, required } from '@angular/forms/signals'
import { AcceptedAiiaConnection, AcceptedEnableBankingConnection } from '@lemon/core-firebase/model/banks'
import { EnableBankingAspspList, EnableBankingAspspSubtype, PSUType } from '@lemon/core-firebase/model/enable-banking-shared'
import { Bic } from '@lemon/core/service/bic.service'
import { AiiaDialogService } from 'app/_angular/service/aiia-dialog.service'
import { EnableBankingDialogService, ViewableEnableBankingAspspSubtype } from 'app/_angular/service/enable-banking-dialog.service'
import { HolviDialogService } from 'app/_angular/service/holvi-dialog.service'
import { asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Observable, Subject } from 'rxjs'

export interface GeneralBankSelectionDialogData {}

interface PankkivalintaLomake {
  accountType: AcceptedAiiaConnection | null
  ebSubtypeName: string | null
  ebPsuType: PSUType | null
}

type DialogBank = 'holvi' | 'op' | 'nordea' | 'danske' | 'handelsbanken' | 'alandsbanken' | 's-pankki' | 'pop-pankki' | 'saastopankki' | 'oma-saastopankki' | 'revolut' | 'alisa-pankki'
type ExpandableBanks = Extract<DialogBank, 'nordea' | 'danske' | 'handelsbanken'>

type BankDialogEntry = {
  name: string
  logo: string
  disabled?: boolean
} & (
  | { provider: 'holvi' }
  | {
      provider: 'aiia'
      /** Aiia bank that needs a personal/business account-type sub-step. */
      expandable?: true
    }
  | { provider: 'enable-banking'; bic: Bic }
)

@Component({
  templateUrl: './general-bank-selection.dialog.html',
  styleUrls: ['./general-bank-selection.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GeneralBankSelectionDialog implements OnInit, OnDestroy {
  @ViewChild('selitys', { static: true }) selitysRef: ElementRef

  valtuutusTextsObservable: Observable<string>

  readonly expandedAiiaBank = signal<ExpandableBanks>(null)

  readonly expandedEbBank = signal<AcceptedEnableBankingConnection>(null)
  readonly currentEbSubtypes = signal<EnableBankingAspspSubtype[]>(null)
  readonly selectedEbSubtype = signal<ViewableEnableBankingAspspSubtype>(null)

  valtuutusTitle: string
  valtuutusStartText: string = this._translationService.lokalisoi('general-bank-selection.palvelun-kaytto-edellyttaa')
  valtuutusEndText: string
  lueLisaa: string

  readonly commonError = signal<string>(undefined)
  extraTextExpanded: boolean = false

  lomake = form(signal<PankkivalintaLomake>({ accountType: null, ebSubtypeName: null, ebPsuType: null }), polku => {
    required(polku.accountType)
    required(polku.ebSubtypeName)
    required(polku.ebPsuType)
  })

  private _buttonWasPushed: boolean
  private _ngUnsubscribe = new Subject<void>()

  dialogConfig: Partial<Record<DialogBank, BankDialogEntry>>
  dialogConfigEntries: ({ key: DialogBank } & BankDialogEntry)[]

  private readonly _ebPankkityypit = signal<EnableBankingAspspList['bankSubtypes']>(null)
  readonly naytettavatPankit = computed(() =>
    this.dialogConfigEntries.filter(entry => entry.provider !== 'enable-banking' || !!this._ebPankkityypit()?.[this._enableBankingDialogService.annaEbAspspBic(entry.bic)]?.length),
  )

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: GeneralBankSelectionDialogData,
    private _dialogRef: MatDialogRef<GeneralBankSelectionDialog>,
    private _translationService: LemonTranslationService,
    private _kayttajaService: KayttajaService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _aiiaDialogService: AiiaDialogService,
    private _holviDialogService: HolviDialogService,
    private _enableBankingDialogService: EnableBankingDialogService,
    private _formValidationService: FormValidationService,
  ) {
    this._dialogRef.disableClose = true

    this.dialogConfig = {
      holvi: {
        provider: 'holvi',
        name: 'Holvi',
        logo: 'https://storage.googleapis.com/lemon-public-images/holvi_logo.png',
      },
      op: {
        provider: 'enable-banking',
        name: 'Osuuspankki',
        logo: '/assets/tunnistautuminen/osuuspankki_button_127x70.png',
        bic: 'OKOYFIHH',
      },
      nordea: {
        provider: 'enable-banking',
        name: 'Nordea',
        logo: '/assets/tunnistautuminen/nordea_button_164x70.png',
        bic: 'NDEAFIHH',
      },
      danske: {
        provider: 'enable-banking',
        name: 'Danske Bank',
        logo: '/assets/tunnistautuminen/danske_bank_button_70x70.png',
        bic: 'DABAFIHH',
      },
      alandsbanken: {
        provider: 'enable-banking',
        name: 'Ålandsbanken',
        logo: '/assets/tunnistautuminen/ab_logo_green.png',
        bic: 'AABAFI22',
      },
      's-pankki': {
        provider: 'enable-banking',
        name: 'S-Pankki',
        logo: '/assets/tunnistautuminen/spankki_button_150x70.png',
        bic: 'SBANFIHH',
      },
      'pop-pankki': {
        provider: 'enable-banking',
        name: 'POP Pankki',
        logo: '/assets/tunnistautuminen/pop_button_60x70.png',
        bic: 'POPFFI22',
      },
      saastopankki: {
        provider: 'enable-banking',
        name: 'Säästöpankki',
        logo: '/assets/tunnistautuminen/saastopankki_button_60x70.png',
        bic: 'ITELFIHH',
      },
      'oma-saastopankki': {
        provider: 'enable-banking',
        name: 'Oma Säästöpankki',
        logo: '/assets/tunnistautuminen/omasaastopankki_button_103x70.png',
        bic: 'ITELFIHH',
      },
      revolut: {
        provider: 'enable-banking',
        name: 'Revolut',
        logo: '/assets/tunnistautuminen/revolut_127x127.png',
        bic: 'REVOLT21XXX',
      },
      'alisa-pankki': {
        provider: 'enable-banking',
        name: 'Alisa Pankki',
        logo: '/assets/tunnistautuminen/alisa_pankki.png',
        bic: 'EVSEFIHH',
      },
    }
    this.dialogConfigEntries = (Object.entries(this.dialogConfig) as [DialogBank, BankDialogEntry][]).map(([key, value]) => ({ key, ...value }))
  }

  ngOnInit() {
    this._lataaEbPankkityypit()
  }

  private async _lataaEbPankkityypit() {
    this._ladataanService.aloitaLataaminen()
    try {
      const pankkityypit = await this._enableBankingDialogService.getAspspDisplayData()
      if (!Object.keys(pankkityypit).length) {
        this._errorHandler.handleError(new Error('Enable Bankingin pankkilista on tyhjä'))
      }
      this._ebPankkityypit.set(pankkityypit)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  closeDialog() {
    this._dialogRef.close()
  }

  onBankClick(key: DialogBank) {
    this.commonError.set(null)
    const cfg = this.dialogConfig[key]
    if (!cfg) {
      this._errorHandler.handleError(new Error('Unknown bank dialog key: ' + key))
      return
    }
    if (cfg.provider === 'holvi') {
      return this.redirectToHolviOauth()
    }
    if (cfg.provider === 'enable-banking') {
      return this.startEnableBankingFlow(key as AcceptedEnableBankingConnection, cfg.bic)
    }
    if (cfg.provider === 'aiia') {
      if (cfg.expandable) {
        return this.expandToAiiaAccountTypes(key as ExpandableBanks)
      }
      return this.redirectToAiiaOauth(key as AcceptedAiiaConnection)
    }
  }

  async expandToAiiaAccountTypes(bank: ExpandableBanks) {
    // Expand the selection to show personal/business account types
    this.expandedAiiaBank.set(bank)
    // Estä edellisen pankin accountType-arvon vuotaminen toiselle pankille
    nollaa(this.lomake.accountType, null)
    this._scrollDialogToTop()
  }

  private _scrollDialogToTop() {
    setTimeout(() => {
      const scrollables = document.querySelectorAll('[mat-dialog-content]')
      if (scrollables?.length > 0) {
        scrollables.forEach(scrollable => scrollable.scroll({ behavior: 'smooth', top: 0, left: 0 }))
      }
    }, 50)
  }

  private _scrollDialogToBottom() {
    setTimeout(() => {
      const scrollables = document.querySelectorAll('[mat-dialog-content]')
      if (scrollables?.length > 0) {
        scrollables.forEach(scrollable => scrollable.scroll({ behavior: 'smooth', top: scrollable.scrollHeight, left: 0 }))
      }
    }, 50)
  }

  backToFullList() {
    this.expandedEbBank.set(null)
    this.expandedAiiaBank.set(null)
    this.currentEbSubtypes.set(null)
    this.selectedEbSubtype.set(null)
    this.commonError.set(null)
    nollaa(this.lomake.accountType, null)
    nollaa(this.lomake.ebSubtypeName, null)
    nollaa(this.lomake.ebPsuType, null)
    this._scrollDialogToTop()
  }

  async startEnableBankingFlow(bank: AcceptedEnableBankingConnection, bic: Bic) {
    const ebAspspData = await this._enableBankingDialogService.getAspspDisplayData()
    let subtypes: EnableBankingAspspSubtype[] = ebAspspData?.[this._enableBankingDialogService.annaEbAspspBic(bic)] ?? []
    if (!subtypes.length) {
      this._errorHandler.handleError(new Error('Could not load ASPSP data for bank: ' + bic))
      return
    }

    /** Säästöpankilla ja Oma Säästöpankilla on sama BIC, ohitetaan ylimääräinen subtype-valinta */
    const rajaaPankkityyppiin = (nimi: string) => {
      const pankinSubtypes = subtypes.filter(subtype => subtype.name === nimi)
      if (!pankinSubtypes.length) {
        this._errorHandler.handleError(new Error(`Enable Bankingin pankkilistassa ei ole pankkityyppiä ${nimi} (${this._enableBankingDialogService.annaEbAspspBic(bic)})`))
        return subtypes
      }
      return pankinSubtypes
    }
    if (bank === 'saastopankki') {
      subtypes = rajaaPankkityyppiin('Säästöpankki')
    } else if (bank === 'oma-saastopankki') {
      subtypes = rajaaPankkityyppiin('Oma Säästöpankki')
    }

    this.expandedEbBank.set(bank)
    this.currentEbSubtypes.set(subtypes)
    nollaa(this.lomake.ebSubtypeName, null)
    nollaa(this.lomake.ebPsuType, null)

    // 1 subtype + multiple psuTypes → skip subtype step, go straight to PSU type picker.
    if (subtypes.length === 1) {
      this._selectSubtype(subtypes[0])
    } else {
      this.selectedEbSubtype.set(null)
    }

    this._scrollDialogToTop()
  }

  onSubtypeSelected(subTypeName: string | undefined) {
    if (!subTypeName) {
      this.selectedEbSubtype.set(null)
      nollaa(this.lomake.ebSubtypeName, null)
      nollaa(this.lomake.ebPsuType, null)
      return
    }
    const subType = this.currentEbSubtypes()?.find(v => v.name === subTypeName)
    if (!subType) {
      this._errorHandler.handleError(new Error('Unknown EB subType selected: ' + subTypeName))
      return
    }
    this._selectSubtype(subType)

    // If subType has only one psuType, skip PSU type step and start the flow.
    if (subType.psuTypes.length === 1) {
      asetaArvo(this.lomake.ebPsuType, subType.psuTypes[0])
    }
  }

  startEbPsuTypeFlow() {
    if (!this.selectedEbSubtype()) {
      this._errorHandler.handleError(new Error('No EB subType selected'))
      return
    }
    if (!this.lomake.ebPsuType().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    const psuType = this.lomake.ebPsuType().value()
    if (psuType) {
      return this.redirectToEnableBankingOauth(this.selectedEbSubtype().name, psuType)
    }
    this._errorHandler.handleError(new Error('No PSU type selected'))
  }

  private _selectSubtype(subType: EnableBankingAspspSubtype) {
    this.selectedEbSubtype.set(this._enableBankingDialogService.convertToSelectedEbSubtype(subType))
    asetaArvo(this.lomake.ebSubtypeName, subType.name)
  }

  async redirectToAiiaOauth(bank: AcceptedAiiaConnection) {
    if (this._buttonWasPushed) {
      return
    }
    this._buttonWasPushed = true
    this.commonError.set(null)

    this._ladataanService.aloitaLataaminen()

    try {
      const tiedot = await this._kayttajaService.getKayttajanTiedot()
      const redirectUrl = await this._aiiaDialogService.fetchAiiaOauthStartUrl(tiedot.asiakasAvain, { bank: bank })
      if (!redirectUrl) {
        throw new Error('No redirectUrl to connect to Aiia')
      }
      location.href = redirectUrl
    } catch (err) {
      this.commonError.set(this._translationService.lokalisoi('general-bank-selection.valtuutuksen-aloitus-epaonnistui'))
      this._scrollDialogToBottom()
      this._errorHandler.handleError(err)
    } finally {
      this._buttonWasPushed = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async redirectToHolviOauth() {
    if (this._buttonWasPushed) {
      return
    }
    this._buttonWasPushed = true
    this.commonError.set(null)

    this._ladataanService.aloitaLataaminen()

    try {
      const tiedot = await this._kayttajaService.getKayttajanTiedot()
      const redirectUrl = await this._holviDialogService.fetchHolviOauthStartUrl(tiedot.asiakasAvain)
      if (!redirectUrl) {
        throw new Error('No redirectUrl to connect to Holvi')
      }
      location.href = redirectUrl
    } catch (err) {
      this.commonError.set(this._translationService.lokalisoi('general-bank-selection.valtuutuksen-aloitus-epaonnistui'))
      this._scrollDialogToBottom()
      this._errorHandler.handleError(err)
    } finally {
      this._buttonWasPushed = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  async redirectToEnableBankingOauth(subtypeName: string, psuType: PSUType) {
    if (this._buttonWasPushed) {
      return
    }
    this._buttonWasPushed = true
    this.commonError.set(null)

    this._ladataanService.aloitaLataaminen()

    try {
      const tiedot = await this._kayttajaService.getKayttaja()
      const redirectUrl = await this._enableBankingDialogService.fetchEnableBankingOauthStartUrl(tiedot.asiakasAvain, subtypeName, psuType, tiedot.asiointikieli)
      if (!redirectUrl) {
        throw new Error('No redirectUrl to connect to Enable Banking')
      }
      location.href = redirectUrl
    } catch (err) {
      this.commonError.set(this._translationService.lokalisoi('general-bank-selection.valtuutuksen-aloitus-epaonnistui'))
      this._scrollDialogToBottom()
      this._errorHandler.handleError(err)
    } finally {
      this._buttonWasPushed = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  getAccountTypeStartAiiaFlow() {
    // Tarkista vain accountType — sama FormGroup sisältää EB-kontrollit, jotka ovat tässä haarassa null
    const accountTypeKentta = this.lomake.accountType
    if (!accountTypeKentta().valid()) {
      accountTypeKentta().markAsTouched()
      return
    }
    const accountType = accountTypeKentta().value()
    if (accountType) {
      return this.redirectToAiiaOauth(accountType)
    }
    this._errorHandler.handleError(new Error('Unknown account type in consent flow ' + accountType))
  }

  // async postpone() {

  //   if (this._buttonWasPushed) {
  //     return
  //   }
  //   this._buttonWasPushed = true

  //   this._ladataanService.aloitaLataaminen()

  //   console.log('TODO! Postponing logic.')

  //   this._buttonWasPushed = false
  //   this._ladataanService.lopetaLataaminen()
  // }

  // expandExtraText() {
  //   this.extraTextExpanded = !this.extraTextExpanded
  //   this._cd.detectChanges()
  //   if (this.extraTextExpanded && this.selitysRef) {
  //     setTimeout(() => {
  //       this.selitysRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' })
  //     }, 10)
  //   }
  // }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
